import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useApp } from '../../AppContext';
import SideHustleLayout from '../../components/SideHustleLayout';

const TakingTheLeap: React.FC = () => {
  const { currentUser } = useApp();
  const [todos, setTodos] = useState<string[]>(['', '', '']);
  const [habitChanges, setHabitChanges] = useState('');
  const [accountability, setAccountability] = useState('');
  const [saving, setSaving] = useState(false);

  // Load data from Firebase
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.takingTheLeap) {
            setTodos(data.takingTheLeap.todos || ['', '', '']);
            setHabitChanges(data.takingTheLeap.habitChanges || '');
            setAccountability(data.takingTheLeap.accountability || '');
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [currentUser]);

  // Save data to Firebase
  const saveData = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const docRef = doc(db, 'sideHustleUsers', currentUser.uid);
      const docSnap = await getDoc(docRef);

      const leapData = {
        todos,
        habitChanges,
        accountability,
        lastUpdated: new Date(),
      };

      if (docSnap.exists()) {
        await setDoc(docRef, {
          ...docSnap.data(),
          takingTheLeap: leapData,
          lastUpdated: new Date(),
        });
      } else {
        await setDoc(docRef, {
          userId: currentUser.uid,
          email: currentUser.email,
          createdAt: new Date(),
          lastUpdated: new Date(),
          takingTheLeap: leapData,
        });
      }
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTodoChange = (index: number, value: string) => {
    const updated = [...todos];
    updated[index] = value;
    setTodos(updated);
  };

  return (
    <SideHustleLayout currentPage="leap">
      <div className="flex flex-col items-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-3xl w-full">
          <h1 className="text-5xl font-bold text-center mb-4">Taking the Leap</h1>
          <p className="text-2xl text-center text-gray-600 mb-12">
            Let's get some momentum
          </p>

          <div className="bg-white p-8 rounded-3xl shadow-lg space-y-8">
            {/* To-do's Section */}
            <div>
              <h2 className="text-2xl font-bold mb-3">To-do's</h2>
              <p className="text-gray-600 mb-4">
                What are three things you need to do to move your side hustle forward?
              </p>

              <div className="space-y-3">
                {todos.map((todo, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="font-semibold text-gray-700">{index + 1}.</span>
                    <input
                      type="text"
                      value={todo}
                      onChange={(e) => handleTodoChange(index, e.target.value)}
                      onBlur={saveData}
                      placeholder="Enter Text"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Habit Changes Section */}
            <div>
              <h2 className="text-lg font-bold mb-3">
                What habit changes do we need to make moving forward?
              </h2>
              <textarea
                value={habitChanges}
                onChange={(e) => setHabitChanges(e.target.value)}
                onBlur={saveData}
                placeholder="Enter Text"
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Accountability Section */}
            <div>
              <h2 className="text-lg font-bold mb-3">
                Put it out there, who are you going to tell about this to keep you accountable?
              </h2>
              <textarea
                value={accountability}
                onChange={(e) => setAccountability(e.target.value)}
                onBlur={saveData}
                placeholder="Enter Text"
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {saving && (
              <div className="text-center text-primary font-semibold">
                Saving...
              </div>
            )}
          </div>
        </div>
      </div>
    </SideHustleLayout>
  );
};

export default TakingTheLeap;
